import { NodeShape } from '../../../../../../../api/com/MetaPattern/MDA/Designer/Shape/NodeShape';
import { BaseElement } from '../../../../../../../api/com/MetaPattern/MDA/MD/Base/BaseElement';
import { Double2D } from '../../../../../../../api/com/MetaPattern/MDA/Designer/Base/Double2D';
import { ValidationResult } from '../../../../../../../api/com/MetaPattern/Core/Infrastructure/Common/ValidationResult';
import { MDShape } from '../../BaseShape/MDShape';
import { Diagram } from '../Diagram';
import { MDExtendService } from '../../../MD/MDExtendService';
import { MDAConstant } from '../../Base/MDAConstant';
import { RectShape } from '../../BaseShape/RectShape';
import { ShapeModel } from '../../BaseShape/ShapeModel';
import { BEComponent } from '../../../../../../../api/com/MetaPattern/MDA/MD/EntityModel/BEComponent';
import { BEReferenceEntity } from '../../../../../../../api/com/MetaPattern/MDA/MD/EntityModel/BEReferenceEntity';
import { BEEntity } from '../../../../../../../api/com/MetaPattern/MDA/MD/EntityModel/BEEntity';

/** 引用类型的图形 */
export class JointReferenceEntityShape extends RectShape {
  /** 重写超类属性 */
  defaults() {
    return {
      ...super.defaults,
      // 定义图形的实际类型
      type: 'mst.JointReferenceEntityShape',
      attrs: {
        rect: { 'stroke-width': 0, fill: '#FFFFFF' },
        '.uml-comment-container': { fill: '#e2e7f5', strokeWidth: 0, stroke: '#545c64', cursor: 'move' },
        '.uml-class-part-rect': { fill: '#e2e7f5', height: 2, cursor: 'move' },
        '.uml-class-row-rect': { fill: '#ffffff', height: 20, cursor: 'move' },
        '.uml-class-name-text': {
          ref: '.uml-comment-container',
          'ref-y': 13,
          'ref-x': 0.5,
          'text-anchor': 'middle',
          'y-alignment': 'middle',
          'font-weight': 'bold',
          fill: 'black',
          'font-size': 12,
          cursor: 'move'
        },
        '.uml-class-row-text': { fontSize: 10, style: 'color:black;cursor:move;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;height:20px;line-height:20px;' },
        '.uml-comment-text': { fontSize: 12 }
      },
      position: { x: 10, y: 10 },
      t_row_rect: '<rect class="uml-class-row-rect" transform="translate(0, {offsetY})" index="{index}" width="{width}"></rect>',
      t_row_text: '<foreignObject class="uml-class-row-text" transform="translate(5, {offsetY})" width="{width}" index="{index}">{text}<title>{text}</title></foreignObject>'
    };
  }

  /**
   * 反序列化构造器，由joint框架默认调用。
   * 1、重写属性
   * 2、设置自定义属性
   * 3、自定义属性反序列化的自行处理
   * @param attributes 从JSON构造
   * @param opt 图形
   */
  constructor(attributes?: any, opt?: any) {
    // 调用超类的构造器
    super(attributes, opt);
    // 重写图形，如果放在defaults上面会覆盖超类，这里只会修改不一致的项，Very Important
    this.attr({
      body: { fill: '#ffffc0', stroke: '#545c64' },
      label: {
        fill: '#000000',
        fontSize: 12
      }
    });
    // 反序列化调用，手工创建的时候不调用
    if (attributes) {
      this.setMD(attributes?.shape, attributes?.model.element, true);
    }
  }

  /**
   * 设置元数据，手工创建图形的时候要额外调用
   * @param elementShape 元数据的图形
   * @param element 元数据
   * @param isFromJSON 是否通过JOINT的JSON构造，这种情况不能从元数据取值
   */
  public setMD(elementShape: NodeShape, element: BEReferenceEntity, isFromJSON: boolean) {
    this.setBaseMD(elementShape, new ShapeModel(element, false), isFromJSON);
  }

  /**
   * 克隆图形，包括图形内部所有元素，不包括与其他图形的关系
   * @param diagram 克隆后所属的图表
   * @param elements 克隆的所有元数据都插入数组返回
   */
  public cloneShape(diagram: Diagram, elements: Array<BaseElement>): MDShape {
    const jShape = new JointReferenceEntityShape();

    const newShape = new NodeShape();
    newShape.copyFrom(this.shape(), false);
    MDExtendService.setCreateInfo(newShape);
    newShape.startPoint = new Double2D(this.position().x + 20, this.position().y + 20);
    newShape.diagram_ID = diagram.diagramShape.id;
    diagram.diagramShape.nodeShapes.push(newShape);

    const newElement = new BEReferenceEntity();

    newElement.copyFrom(this.referenceEntity, false);
    MDExtendService.setCreateInfo(newElement);
    elements.push(newElement);
    if (diagram.component instanceof BEComponent) {
      newElement.component_ID = diagram.component.id;
      diagram.component.referenceEntities.push(newElement);
    }

    newShape.elementId = newElement.designId;
    jShape.setMD(newShape, newElement, false);
    diagram.cells.push(jShape);
    return jShape;
  }

  get referenceEntity(): BEReferenceEntity {
    return this.element() as BEReferenceEntity;
  }

  get displayElement() {
    if (this.referenceEntity.entity) {
      return this.referenceEntity.entity;
    }
    return this.referenceEntity;
  }

  shapeType(): string {
    return MDAConstant.Type.ADD_REFERENCEENTITY;
  }

  doAction(action: string): BaseElement | null {
    return super.doAction(action);
  }

  /** 添加子元素的克隆 */
  addChildClone(element: BaseElement, elements: Array<BaseElement>): BaseElement | null {
    return null;
  }

  /** 根据链接的类型，设置鼠标的样式 */
  setLinkType(linkType: string) {
    if (linkType !== '') {
      this.attr({ '.uml-comment-container': { cursor: 'not-allowed' }, '.uml-comment-text': { cursor: 'not-allowed' } });
    } else {
      this.attr({ '.uml-comment-container': { cursor: 'move' }, '.uml-comment-text': { cursor: 'move' } });
    }
  }

  /** 输出图形的代码 */
  protected updateRectangles(setWidth?: number, setHeight?: number) {
    const model = this.get('model') as ShapeModel;
    if (model == null) {
      return;
    }
    let height = this.size().height;
    if (setHeight) {
      height = setHeight;
    } else if (height === 0) {
      height = 60;
    }
    if (height < 60) {
      height = 60;
    }
    let width = this.size().width;
    if (setWidth) {
      width = setWidth;
    }
    if (width < 180) {
      width = 180;
    }
    const t_row_rect = this.get('t_row_rect');
    const t_row_text = this.get('t_row_text');
    const lineHeight = 20;
    let offsetY = 25;
    const markup: Array<string> = new Array<string>();
    markup.push('<g>');
    markup.push(`<rect class="uml-comment-container" rx="5" ry="5" width="${width}" height="${height}"/>`);
    markup.push(`<rect class="uml-class-part-rect" transform="translate(0, ${offsetY})"/>`);
    offsetY += 4;
    markup.push(t_row_rect.replace('{offsetY}', `${offsetY + 4}`).replace('{index}', 2).replace('{width}', width));
    markup.push(t_row_text.replaceAll('{text}', `名空间: ${(this.displayElement instanceof BEEntity ? this.displayElement.component.rootNamespace + '.' + this.displayElement.name : '')}`).replace('{offsetY}', offsetY + 4).replace('{index}', 2).replace('{width}', width - 5));
    offsetY += lineHeight;
    markup.push(`<text class="uml-class-name-text">${this.getDisplayTitle(this.displayElement)}(引用)</text>`);
    // markup.push(`<div style="display:none">${this.displayElement.description}</div>`);
    markup.push('</g>');
    this.set('markup', markup.join(''));
    this.size(width, height);
    this.trigger('uml-update');
  }

  /** 校验模型 */
  public validate(): Array<ValidationResult> {
    return super.validate();
  }
}
